import React, { forwardRef, useEffect, useRef } from 'react';
import { cn } from './utils/cn';
import { PlayingCard } from './PlayingCard';
import { ChipStack } from './PokerChip';
import { useAudio } from './AudioProvider';

/**
 * @param {{ suit: string, rank: string } | null}[] communitySlots length 5
 * @param {boolean} [showCommunity=true] — ряд борда (флоп…ривер). В секе общих карт нет — передавайте `false`.
 * @param {boolean} [showDeck=false] — при `showCommunity=false` показать стек колоды (рубашки) рядом с POT; `ref` вешается на этот стек для анимации раздачи.
 * @param {number|null} [deckCount] — подпись «осталось карт» (онлайн-снапшот).
 * @param {number|string} [deckPulseKey=0] — смена ключа при новой раздаче (лёгкая анимация стека).
 * @param {import('react').ReactNode} [deckLayerExtra] — слой поверх области колоды (внутри контейнера с `ref`), например слот козыря.
 * @param {'classic'|'photo'} [feltVariant='classic'] — `photo`: прозрачное сукно поверх фото-фона (белот «игра»).
 */
export const PokerTableSurface = forwardRef(function PokerTableSurface(
  {
    communitySlots,
    pot,
    className,
    potLabel = 'POT',
    showCommunity = true,
    showDeck = false,
    deckCount = null,
    deckPulseKey = 0,
    potAnchorRef = null,
    deckLayerExtra = null,
    feltVariant = 'classic',
  },
  ref
) {
  const isPhotoFelt = feltVariant === 'photo';
  const { play } = useAudio();
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (!showCommunity) return;
    const n = communitySlots.filter(Boolean).length;
    if (n > prevCountRef.current) {
      const delta = n - prevCountRef.current;
      for (let i = 0; i < delta; i++) {
        setTimeout(() => play('community-card'), i * 150);
      }
    }
    prevCountRef.current = n;
  }, [communitySlots, play, showCommunity]);

  const potReadability = isPhotoFelt ? 'drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]' : '';

  const potBlock =
    pot > 0 ? (
      <ChipStack total={pot} compact={false} maxChipsPerColumn={8} currencyLabel={potLabel} />
    ) : (
      <div className={cn('flex flex-col items-center', isPhotoFelt && potReadability)}>
        <span
          className={cn(
            'text-[9px] uppercase tracking-wide',
            isPhotoFelt ? 'text-white/90' : 'text-white/60',
          )}
        >
          {potLabel}
        </span>
        <span
          className={cn(
            'text-base font-bold text-neon-gold font-mono',
            isPhotoFelt
              ? 'drop-shadow-[0_1px_3px_rgba(0,0,0,0.95),0_0_12px_rgba(255,215,0,0.45)]'
              : 'drop-shadow-[0_0_10px_rgba(255,215,0,0.35)]',
          )}
        >
          0
        </span>
      </div>
    );

  const deckStack =
    !showCommunity && showDeck ? (
      <div
        className="pointer-events-none absolute bottom-[9%] right-[9%] z-[1] flex flex-col items-center sm:bottom-[11%] sm:right-[11%]"
        aria-hidden
      >
        <div
          key={String(deckPulseKey)}
          ref={ref}
          className="relative h-[68px] w-[52px] origin-bottom-right scale-[0.7]"
        >
          {deckLayerExtra}
          <PlayingCard faceDown size="sm" className="absolute left-0 top-0 z-[3] shadow-lg" />
          <PlayingCard faceDown size="sm" className="absolute left-[3px] top-[3px] z-[4] shadow-lg" />
          <PlayingCard faceDown size="sm" className="absolute left-[6px] top-[6px] z-[5] shadow-lg" />
        </div>
        {deckCount != null && Number.isFinite(Number(deckCount)) ? (
          <p
            className={cn(
              'mt-0.5 font-mono text-[9px] font-semibold tabular-nums',
              isPhotoFelt ? 'text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]' : 'text-white/55',
            )}
          >
            {Math.max(0, Math.floor(Number(deckCount)))}
          </p>
        ) : null}
      </div>
    ) : null;

  return (
    <div
      className={cn(
        'relative mx-auto aspect-[4/3] w-full max-w-lg md:max-w-xl',
        'max-h-[min(340px,42svh)] min-h-[160px]',
        isPhotoFelt
          ? cn(
              'rounded-[100px] border border-emerald-400/25 bg-transparent',
              'shadow-none ring-0',
            )
          : cn(
              'rounded-[100px] border-4 border-border',
              'bg-gradient-to-br from-table-green to-green-950',
              'shadow-[inset_0_0_60px_rgba(0,0,0,0.5),0_0_30px_rgba(0,255,255,0.15)]',
            ),
        'overflow-hidden',
        className
      )}
    >
      {!isPhotoFelt ? (
        <>
          <div className="absolute inset-2 rounded-[90px] border border-neon-cyan/20" />
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,0,0,0.3)_100%)]" />
        </>
      ) : null}

      <div
        className={cn(
          'absolute inset-0 flex flex-col items-center justify-center px-4',
          showCommunity ? 'gap-3' : 'gap-0'
        )}
      >
        {!showCommunity && showDeck ? (
          <>
            <div ref={potAnchorRef} className="flex w-full flex-col items-center justify-center translate-y-2">
              {potBlock}
            </div>
            {deckStack}
          </>
        ) : (
          <div ref={potAnchorRef} className="flex w-full flex-col items-center justify-center">
            {potBlock}
          </div>
        )}

        {showCommunity && (
          <div className="flex flex-wrap justify-center gap-1">
            {[0, 1, 2, 3, 4].map((index) => (
              <div key={index} className="relative">
                {communitySlots[index] ? (
                  <PlayingCard
                    suit={communitySlots[index].suit}
                    rank={communitySlots[index].rank}
                    size="sm"
                  />
                ) : (
                  <div className="h-16 w-12 rounded-lg border border-dashed border-white/20 bg-black/20" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!isPhotoFelt ? (
        <>
          <div className="absolute bottom-0 left-1/4 right-1/4 h-[2px] bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent" />
          <div className="absolute top-0 left-1/4 right-1/4 h-[2px] bg-gradient-to-r from-transparent via-neon-orange/30 to-transparent" />
        </>
      ) : null}
    </div>
  );
});

PokerTableSurface.displayName = 'PokerTableSurface';
